'use client';

import { useEffect, useState, useCallback } from 'react';
import { Alert, StyleSheet } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withRepeat,
  runOnJS,
  Easing,
} from 'react-native-reanimated';

// Styled‑components & assets -------------------------------------------------
import {
  Background,
  Container,
  BrightWheelImage,
  SpinningWheelImage,
  StaticWheelImage,
  WheelContainer,
  Content,
  GirosContainer,
  GirosText,
  LogoWheel,
  AwesomeButtonText,
  SpinButtonContainer,
  Header,
  CloseIconButton,
  CloseIconImage,
  WheelTitlesContainer,
  WheelTitle,
  WheelSubtitle,
  GirosTextLabel,
  LockContainer,
  LockIcon,
  HelpButton,
  HelpIconImage,

} from './styles';

import AwesomeButton from 'react-native-really-awesome-button';

import roleta from '../../assets/images/roleta/roleta.webp';
import rodaRoleta from '../../assets/images/roleta/roda-roleta.webp';
import brilho from '../../assets/images/roleta/brilho.webp';
import fundoRoleta from '../../assets/images/roleta/background_roleta.webp';
import logoRoleta from '../../assets/images/roleta/logo-roleta-baratao.webp';
import closeRoulette from '../../assets/images/roleta/close-roulette.webp';
import helpRoulette from '../../assets/images/roleta/help.webp';

import { WinPrizeModal } from './components/WinPrizeModal';

import { useTheme } from 'styled-components';
import { useNavigation } from '@react-navigation/native';
import { RFValue } from 'react-native-responsive-fontsize';
import LinearGradient from 'react-native-linear-gradient';
import { api } from '../../services/api';
import type { RouletteDTO } from '../../dtos/RouletteDTO';
import { RouletteBottomSheet } from '../../components/shared/RouletteBottomSheet';
import { useGlobal } from '../../context/global';
import { Maintenance } from '../../components/Interface/Maintenance';

// Reanimated wrappers --------------------------------------------------------
const AnimatedSpinningWheelImage = Animated.createAnimatedComponent(SpinningWheelImage as any);
const AnimatedBrightWheelImage = Animated.createAnimatedComponent(BrightWheelImage as any);

export function Roulette() {
  const theme = useTheme();
  const navigation = useNavigation();

  // Shared animated values ---------------------------------------------------
  const swingAngle = useSharedValue(-8); // começa em -8° para suavizar o ciclo
  const spinAngle = useSharedValue(0); // giro real acumulado
  const glowOpacity = useSharedValue(0); // brilho intermitente
  const timePulseScale = useSharedValue(1); // pulsação do tempo restante

  // Local state --------------------------------------------------------------
  const [spinning, setSpinning] = useState(false);
  const [, setPrize] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [rouletteData, setRouletteData] = useState<RouletteDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<string>('00:00:00');
  const [hasError, setHasError] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const {disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);

  // 1. Swing loop ------------------------------------------------------------
  useEffect(() => {
    swingAngle.value = withRepeat(withTiming(8, { duration: 1500, easing: Easing.inOut(Easing.ease) }), -1, true);
  }, []);

  // 2. Glow loop -------------------------------------------------------------
  useEffect(() => {
    glowOpacity.value = withRepeat(withTiming(1, { duration: 600, easing: Easing.linear }), -1, true);
  }, []);

  // 3. Time pulse animation --------------------------------------------------
  useEffect(() => {
    // Pulsa sempre que há dados da roleta carregados
    if (rouletteData && !loading && !hasError) {
      timePulseScale.value = withRepeat(withTiming(1.1, { duration: 800, easing: Easing.inOut(Easing.ease) }), -1, true);
    } else {
      // Sem pulsação durante loading ou sem dados
      timePulseScale.value = withTiming(1, {
        duration: 300,
        easing: Easing.out(Easing.ease),
      });
    }
  }, [rouletteData, loading, hasError]);

  // 4. Derived styles --------------------------------------------------------
  const wheelAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${swingAngle.value + spinAngle.value}deg` }],
  }));

  const glowAnimatedStyle = useAnimatedStyle(() => ({
    opacity: glowOpacity.value,
  }));

  const timePulseAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: timePulseScale.value }],
  }));

  // 5. Helpers ---------------------------------------------------------------
  const calculatePrize = (angle: number) => {
    'worklet';
    const sectors = 8;
    const sectorDeg = 360 / sectors;
    const idx = Math.floor((angle % 360) / sectorDeg);
    return `Prêmio ${idx + 1}`;
  };



  const parseTimeString = (timeString: string): number => {
    if (!timeString || timeString === '00:00:00') {return 0;}

    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const formatSecondsToTime = (totalSeconds: number): string => {
    if (totalSeconds <= 0) {return '00:00:00';}

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${seconds.toString().padStart(2, '0')}`;
    }
  };

  const handleSpinEnd = (finalAngle: number) => {
    // Evita abrir o modal se já estiver aberto
    if (showModal) {
      console.log('Modal já está aberto, ignorando handleSpinEnd');
      return;
    }

    const prizeName = calculatePrize(finalAngle);
    setPrize(prizeName);
    setSpinning(false);
    setShowModal(true);
  };

  // 5. Spin action -----------------------------------------------------------
  const spinWheel = async () => {
    // Verifica se já girou e se ainda há tempo restante
    if (rouletteData?.jaRodou && timeRemaining !== '00:00:00') {
      Alert.alert('Giro indisponível', 'Você já girou a roleta hoje. Aguarde o tempo restante para girar novamente.');
      return;
    }
    if (spinning || loading || hasError) {return;}

    setSpinning(true);

    try {
      // Chama a API para girar a roleta
      const response = await api.post<RouletteDTO>('/roleta');

      // Atualiza os dados da roleta com a resposta
      setRouletteData(response.data);

      // Atualiza o contador de tempo
      if (response.data.tempo_restante) {
        setTimeRemaining(response.data.tempo_restante);
      }

      // Inicia a animação da roleta - botão permanece bloqueado
      const randomExtra = Math.floor(Math.random() * 360 + 1440); // 1440°–1800°
      const target = spinAngle.value + randomExtra;

      spinAngle.value = withTiming(target, { duration: 6000, easing: Easing.out(Easing.cubic) }, (finished) => {
        if (finished) {
          runOnJS(handleSpinEnd)(target);
          spinAngle.value = 0; // reset
        }
      });
    } catch (error: any) {
      console.log('🚀 ~ spinWheel ~ error:', error?.request || error);
      Alert.alert('Erro', 'Não foi possível girar a roleta. Tente novamente.');
      setSpinning(false);
    }
  };

  const getRouletteStatus = useCallback(async () => {
    try {
      setLoading(true);
      setHasError(false);
      const response = await api.get<RouletteDTO>('/roleta');
      setRouletteData(response.data);

      // Inicializa o contador de tempo se houver tempo restante
      if (response.data.tempo_restante && response.data.tempo_restante !== '00:00:00') {
        setTimeRemaining(response.data.tempo_restante);
      } else {
        setTimeRemaining('00:00:00');
      }
    } catch (error: any) {
      console.log('🚀 ~ getRouletteStatus ~ error:', error?.request || error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getRouletteStatus();
  }, [getRouletteStatus]);

  // Contador em tempo real
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timeRemaining && timeRemaining !== '00:00:00' && !hasError) {
      interval = setInterval(() => {
        setTimeRemaining((prevTime) => {
          const totalSeconds = parseTimeString(prevTime);

          if (totalSeconds <= 1) {
            // Tempo zerou, não chama getRouletteStatus aqui para evitar loop
            return '00:00:00';
          }

          return formatSecondsToTime(totalSeconds - 1);
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timeRemaining, hasError]);

  // Atualiza o status da roleta quando o tempo restante zerar
  useEffect(() => {
    if (timeRemaining === '00:00:00' && rouletteData?.jaRodou && !hasError && !showModal) {
      // Quando o tempo zerar, atualiza o status para permitir novo giro
      // Só atualiza se o modal não estiver aberto para evitar interferência
      getRouletteStatus();
    }
  }, [timeRemaining, rouletteData?.jaRodou, hasError, showModal]);

  // 6. Modal -----------------------------------------------------------------
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // 7. Render ----------------------------------------------------------------
  if (isDisabled('roleta')) {
    return <Maintenance />;
  }

  return (
    <Background source={fundoRoleta}>
      {/* Header */}
      <Header>
        <CloseIconButton
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Initial'))}
        >
          <CloseIconImage source={closeRoulette} />
        </CloseIconButton>

         <HelpButton
          onPress={() => setShowHelpModal(true)}
        >

          <HelpIconImage source={helpRoulette} />


        </HelpButton>
      </Header>

      <Container>
        <Content>
          <LogoWheel source={logoRoleta} resizeMode="contain" />

          {/* Titles */}
          <WheelTitlesContainer>
            <WheelTitle>
              {loading
                ? 'Carregando...'
                : hasError
                  ? 'Roleta Indisponível'
                  : rouletteData?.jaRodou && timeRemaining !== '00:00:00'
                    ? 'Você já girou a roleta hoje'
                    : 'Gire a roda e ganhe prêmios exclusivos'}
            </WheelTitle>
            <WheelSubtitle>
              {loading
                ? 'Aguarde um momento'
                : hasError
                  ? 'Tente novamente mais tarde'
                  : rouletteData?.jaRodou && timeRemaining !== '00:00:00'
                    ? 'Aguarde o tempo restante para girar novamente'
                    : 'Você pode ganhar baratinhaZ e cupons de desconto a cada giro.'}
            </WheelSubtitle>
          </WheelTitlesContainer>

          {/* Wheel */}
          <WheelContainer>
            <AnimatedBrightWheelImage
              //@ts-ignore
              source={brilho}
              style={glowAnimatedStyle}
              resizeMode="cover"
            />

            <StaticWheelImage source={rodaRoleta} resizeMode="cover" />

            <AnimatedSpinningWheelImage
              //@ts-ignore
              source={roleta}
              style={wheelAnimatedStyle}
              resizeMode="cover"
            />

            <SpinButtonContainer>
              <AwesomeButton
                extra={
                  <LinearGradient colors={theme.colors.spin_button_gradient} style={StyleSheet.absoluteFillObject} />
                }
                onPress={spinWheel}
                width={RFValue(60)}
                height={RFValue(60)}
                disabled={
                  (rouletteData?.jaRodou && timeRemaining !== '00:00:00') ||
                  spinning || // Inclui tanto a chamada do backend quanto a animação
                  loading ||
                  hasError
                }
                borderColor="#dddada"
                borderWidth={3}
                textFontFamily={theme.fonts.semibold}
                backgroundDarker="#6d0f9c"
                borderRadius={RFValue(50)}
              >
                {loading ? (
                  <AwesomeButtonText>...</AwesomeButtonText>
                ) : hasError ? (
                  <LockContainer>
                    <LockIcon name="lock" />
                  </LockContainer>
                ) : (rouletteData?.jaRodou && timeRemaining !== '00:00:00') || spinning ? (
                  <LockContainer>
                    <LockIcon name="lock" />
                  </LockContainer>
                ) : (
                  <AwesomeButtonText>GIRAR</AwesomeButtonText>
                )}
              </AwesomeButton>
            </SpinButtonContainer>
          </WheelContainer>

          {/* Countdown */}
          <Animated.View style={timePulseAnimatedStyle}>
            <GirosContainer colors={theme.colors.spins_text_background}>
              <GirosTextLabel>TEMPO ATÉ O PRÓXIMO GIRO</GirosTextLabel>
              <GirosText>
                {loading ? '00:00:00' : hasError ? 'Indisponível' : !rouletteData ? '00:00:00' : timeRemaining}
              </GirosText>
            </GirosContainer>
          </Animated.View>
        </Content>
      </Container>

      <WinPrizeModal isVisible={showModal} onClose={handleCloseModal} rouletteData={rouletteData} />
      <RouletteBottomSheet isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </Background>
  );
}
